import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from 'lucide-react'
import { apiClient } from '@/services/api/client'

interface PdfPanelProps {
  fileId: string | null
  currentPage: number
  onPageChange: (page: number) => void
  allowedPages?: number[]
}

export function PdfPanel({ fileId, currentPage, onPageChange, allowedPages }: PdfPanelProps) {
  const [zoom, setZoom] = useState(1.0)
  const [imgSrc, setImgSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalPages, setTotalPages] = useState<number | null>(null)

  const loadPage = useCallback(async (pageNum: number) => {
    if (!fileId) return
    setLoading(true)
    setError(null)
    try {
      const resp = await apiClient.get(`/api/v1/files/${fileId}/page/${pageNum}/image`, {
        params: { scale: 2.0 },
        responseType: 'blob',
      })
      const totalPagesHeader = resp.headers['x-total-pages']
      if (totalPagesHeader) {
        setTotalPages(parseInt(String(totalPagesHeader), 10))
      }

      const url = URL.createObjectURL(resp.data)
      setImgSrc(prev => {
        if (prev) URL.revokeObjectURL(prev)
        return url
      })
    } catch (err: unknown) {
      const ax = axios.isAxiosError(err) ? err : null
      if (ax && (ax.response?.status === 400 || ax.response?.status === 404)) {
        const totalPagesHeader = ax.response.headers['x-total-pages']
        if (totalPagesHeader) {
          setTotalPages(parseInt(String(totalPagesHeader), 10))
        }
        setError('Seite nicht verfügbar')
        return
      }
      setError(err instanceof Error ? err.message : 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }, [fileId])

  useEffect(() => {
    setTotalPages(null)
  }, [fileId])

  useEffect(() => {
    loadPage(currentPage)
  }, [currentPage, loadPage])

  useEffect(() => {
    return () => {
      if (imgSrc) URL.revokeObjectURL(imgSrc)
    }
  }, [imgSrc])

  const handleZoomIn = () => setZoom(z => z + 0.25)
  const handleZoomOut = () => setZoom(z => Math.max(0.5, z - 0.25))

  const pageList = allowedPages && allowedPages.length > 0 ? allowedPages : null
  const pagePos = pageList ? pageList.indexOf(currentPage) : -1
  const canPrev = pageList ? pagePos > 0 : currentPage > 1
  const canNext = pageList
    ? pagePos >= 0 && pagePos < pageList.length - 1
    : !loading && (totalPages === null || currentPage < totalPages)
  const pageLabel = pageList
    ? `PDF ${currentPage} · ${Math.max(pagePos, 0) + 1}/${pageList.length}`
    : `Seite ${currentPage}${totalPages !== null ? ` von ${totalPages}` : ''}`

  const goPrev = () => {
    if (pageList) {
      const prev = pagePos > 0 ? pageList[pagePos - 1] : undefined
      if (prev !== undefined) onPageChange(prev)
      return
    }
    onPageChange(Math.max(1, currentPage - 1))
  }

  const goNext = () => {
    if (pageList) {
      const next = pagePos >= 0 && pagePos < pageList.length - 1 ? pageList[pagePos + 1] : undefined
      if (next !== undefined) onPageChange(next)
      return
    }
    onPageChange(currentPage + 1)
  }

  if (!fileId) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
        <span className="text-sm font-medium text-gray-700">{pageList ? 'Relevante Seiten' : 'Original-PDF'}</span>
        </div>
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          Keine PDF verfügbar
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b shrink-0">
        <span className="text-sm font-medium text-gray-700">Original-PDF</span>
        <div className="flex items-center gap-1">
          <button
            onClick={goPrev}
            disabled={!canPrev}
            className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-600 min-w-24 text-center">
            {pageLabel}
          </span>
          <button
            onClick={goNext}
            disabled={!canNext}
            className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={handleZoomOut} className="p-1 hover:bg-gray-200 rounded">
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-600 w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={handleZoomIn} className="p-1 hover:bg-gray-200 rounded">
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-gray-100 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
            {error}
          </div>
        )}
        {imgSrc && !loading && (
          <div className="overflow-auto min-h-full p-4">
            <img
              src={imgSrc}
              alt={`PDF Seite ${currentPage}`}
              className="transition-transform duration-150"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
              draggable={false}
            />
          </div>
        )}
      </div>
    </div>
  )
}
